<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { UFPMSubTab, UFPMMember, UFPMFilter } from './lib/types'
import { getFilteredMembers } from './lib/ufpmHelpers'
import { useUFPM } from './composables/useUFPM'
import { RANK_OPTIONS } from './lib/ranks'
import UFPMSidebar from './components/UFPMSidebar.vue'
import UFPMMemberProfile from './components/UFPMMemberProfile.vue'

const {
  filteredMembers,
  members,
  selectedMemberId,
  selectedMember,
  subTab,
  editingMemberId,
  filters,
  search,
  uniqueCurrencies,
  uniqueCategories,
  dueMonthOptions,
  isUploading,
  uploadError,
  handleUpload,
  submitMemberForm,
  selectMember,
  deleteMember,
  clearRoster,
  showMemberPicker,
  closeMemberPicker,
  pickerSource,
} = useUFPM()

function onUpdateFilters(v: Partial<UFPMFilter>) {
  filters.value = { ...filters.value, ...v }
}
function onClearFilters() {
  filters.value = { fitnessCurrency: '', assessmentCategory: '', dueMonth: '' }
}

// ---- Add / Edit Member form ----

const showAddEditModal = ref(false)
const formData         = ref<Partial<UFPMMember>>({})
const formError        = ref('')
const isEditing        = computed(() => !!formData.value.id)

function openAddMemberModal() {
  formData.value         = {}
  formError.value        = ''
  editingMemberId.value  = null
  showAddEditModal.value = true
}
function closeAddEditModal() {
  showAddEditModal.value = false
  editingMemberId.value  = null
  formData.value         = {}
  formError.value        = ''
}
function handleFormSubmit() {
  const firstName = (formData.value.firstName || '').trim()
  const lastName  = (formData.value.lastName  || '').trim()
  if (!firstName && !lastName) { formError.value = 'Enter at least a first or last name.'; return }
  formError.value = ''
  submitMemberForm(formData.value)
  closeAddEditModal()
}

// ---- View navigation (roster-only: no embedded dashboard) ----

const currentView = ref<'roster' | 'profile'>('roster')

function goToProfile() { currentView.value = 'profile'; window.scrollTo(0, 0) }
function goToRoster()  { currentView.value = 'roster' }

watch(selectedMember, (m) => {
  if (!m && currentView.value === 'profile') goToRoster()
})

function handleEditFromProfile() {
  if (!selectedMember.value) return
  formData.value        = { ...selectedMember.value }
  editingMemberId.value = selectedMember.value.id
  formError.value       = ''
  showAddEditModal.value = true
}
function handleDeleteFromProfile() {
  if (selectedMemberId.value) deleteMember(selectedMemberId.value, true)
  goToRoster()
}
function handleSwitchToCalc() {
  window.ufpmTools?.switchToCalculatorForMember?.()
}
function handleSwitchToPlan() {
  window.ufpmTools?.switchToPlanForMember?.()
}

// ---- Member Picker ----

const EMPTY_FILTERS: UFPMFilter = { fitnessCurrency: '', assessmentCategory: '', dueMonth: '' }
const pickerSearch  = ref('')
const pickerResults = computed(() =>
  getFilteredMembers(members.value as UFPMMember[], pickerSearch.value, EMPTY_FILTERS).slice(0, 10)
)

function handlePickerSelect(id: string) {
  const source = pickerSource.value
  selectMember(id)
  closeMemberPicker()
  pickerSearch.value = ''
  if (source === 'calculator') {
    const calcTab = document.getElementById('tab-calc') as HTMLElement | null
    if (calcTab && typeof window.switchTab === 'function') window.switchTab('calc', calcTab)
  }
}
function closePickerAndReset() {
  closeMemberPicker()
  pickerSearch.value = ''
}
</script>

<template>
  <div class="ufpm-shell">

    <!-- VIEW 0: Roster -->
    <UFPMSidebar
      v-show="currentView === 'roster'"
      :filteredMembers="filteredMembers"
      :totalCount="members.length"
      :search="search"
      :filters="filters"
      :uniqueCurrencies="uniqueCurrencies"
      :uniqueCategories="uniqueCategories"
      :dueMonthOptions="dueMonthOptions"
      :selectedMemberId="selectedMemberId"
      :isUploading="isUploading"
      :uploadError="uploadError"
      @update:search="search = $event"
      @update:filters="onUpdateFilters"
      @clearFilters="onClearFilters"
      @upload="handleUpload"
      @selectMember="selectMember"
      @deleteMember="(id) => deleteMember(id, true)"
      @submitForm="submitMemberForm"
      @cancelEdit="editingMemberId = null"
      @openAddMemberModal="openAddMemberModal"
      @viewProfile="goToProfile"
      @clearRoster="clearRoster"
    />

    <!-- VIEW 1: Member Profile -->
    <UFPMMemberProfile
      v-if="selectedMember"
      v-show="currentView === 'profile'"
      :member="selectedMember"
      :subTab="subTab"
      @update:subTab="(val: UFPMSubTab) => (subTab = val)"
      @back="goToRoster"
      @editMember="handleEditFromProfile"
      @deleteMember="handleDeleteFromProfile"
      @switchToCalc="handleSwitchToCalc"
      @switchToPlan="handleSwitchToPlan"
    />

    <!-- Member Picker overlay -->
    <div v-if="showMemberPicker" class="ufpm-modal-backdrop" @click.self="closePickerAndReset">
      <div class="ufpm-modal-panel">
        <div class="ufpm-modal-head">
          <span class="acc-title" style="margin:0;">Select Member</span>
          <button class="ufpm-modal-close" type="button" aria-label="Close" @click="closePickerAndReset">✕</button>
        </div>
        <div class="field">
          <label for="ufpm-picker-search-rs">Search</label>
          <input id="ufpm-picker-search-rs" type="text" placeholder="Name, rank, organization…" autocomplete="off" v-model="pickerSearch" />
        </div>
        <div class="ufpm-picker-list">
          <p v-if="!pickerResults.length" class="ufpm-empty">No members found.</p>
          <button
            v-for="m in pickerResults"
            :key="m.id"
            class="ufpm-picker-item"
            type="button"
            @click="handlePickerSelect(m.id)"
          >
            <span class="ufpm-member-name">{{ m.fullName || 'Unnamed Member' }}</span>
            <span class="ufpm-member-meta">{{ [m.rank, m.organization].filter(Boolean).join(' | ') || 'No unit data' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Add / Edit Member modal -->
    <div v-if="showAddEditModal" class="ufpm-modal-backdrop" @click.self="closeAddEditModal">
      <div class="ufpm-modal-panel">
        <div class="ufpm-modal-head">
          <span class="acc-title" style="margin:0;">{{ isEditing ? 'Edit Member' : 'Add Member' }}</span>
          <button class="ufpm-modal-close" type="button" aria-label="Close" @click="closeAddEditModal">✕</button>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 12px;">
          <div class="field"><label for="mf-last-rs">Last Name</label><input id="mf-last-rs" type="text" autocomplete="off" v-model.trim="formData.lastName" /></div>
          <div class="field"><label for="mf-first-rs">First Name</label><input id="mf-first-rs" type="text" autocomplete="off" v-model.trim="formData.firstName" /></div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 12px;">
          <div class="field">
            <label for="mf-rank-rs">Rank</label>
            <select id="mf-rank-rs" v-model="formData.rank">
              <option value="">-- Select --</option>
              <option v-for="r in RANK_OPTIONS" :key="r" :value="r">{{ r }}</option>
            </select>
          </div>
          <div class="field">
            <label for="mf-sex-rs">Sex</label>
            <select id="mf-sex-rs" v-model="formData.sex">
              <option value="">Unknown</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
        </div>
        <div class="field"><label for="mf-org-rs">Organization / Squadron</label><input id="mf-org-rs" type="text" autocomplete="off" v-model.trim="formData.organization" /></div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 12px;">
          <div class="field"><label for="mf-dob-rs">Date of Birth</label><input id="mf-dob-rs" type="date" v-model="formData.dob" /></div>
          <div class="field"><label for="mf-due-rs">Next Assessment Due</label><input id="mf-due-rs" type="date" v-model="formData.nextFitnessAssessmentDueDate" /></div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 12px;">
          <div class="field"><label for="mf-currency-rs">Fitness Currency</label><input id="mf-currency-rs" type="text" autocomplete="off" placeholder="e.g. Current" v-model.trim="formData.fitnessCurrency" /></div>
          <div class="field"><label for="mf-email-rs">Email (optional)</label><input id="mf-email-rs" type="email" autocomplete="off" v-model.trim="formData.email" /></div>
        </div>

        <p v-if="formError" class="ufpm-form-error">{{ formError }}</p>
        <div class="ufpm-form-actions" style="margin-top:4px;">
          <button class="btn btn-primary" type="button" @click="handleFormSubmit">{{ isEditing ? 'Save Changes' : 'Add Member' }}</button>
          <button class="btn btn-secondary" type="button" @click="closeAddEditModal">Cancel</button>
        </div>
      </div>
    </div>

  </div>
</template>
